import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useCrashTrackAssetInfoQuery } from "../../api/network/adminApiServices";
import svgIcons from "../../assets/svg/svgIcons";
import { useLoggedInUserData } from "../../utils/user";
import { crashtrack_tab_slugs } from "./crash-track-admin-details";
import { CrashTrackTabParams } from "./type";
import { APP_CONFIG } from "../../constants/constants";

const ScreenCrashTrackTabAssetInfo = ({
  assetCrashIdAsset,
  assetCrashIdTimestamp,
  onLoadComplete,
  printMode,
}: CrashTrackTabParams) => {
  const tab_slug = crashtrack_tab_slugs[0]; // asset_info
  const { t: tCrashTrackDetails } = useTranslation("translation", {
    keyPrefix: `admins.crashtrack.detailsPage`,
  });
  const { t } = useTranslation("translation", {
    keyPrefix: `admins.crashtrack.detailsPage.tab_${tab_slug}`,
  });
  const hasCalledLoadComplete = useRef(false);

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const {
    data: dataCrashTrackAssetInfo,
    isFetching: isFetchingCrashTrackAssetInfo,
    isError: isErrorCrashTrackAssetInfo,
  } = useCrashTrackAssetInfoQuery(
    { assetId: assetCrashIdAsset, timestamp: assetCrashIdTimestamp },
    { skip: !assetCrashIdAsset || !assetCrashIdTimestamp }
  );
  useEffect(() => {
    if (
      printMode &&
      !isFetchingCrashTrackAssetInfo &&
      !hasCalledLoadComplete.current
    ) {
      onLoadComplete?.();
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          "===== CRASH TRACK TAB LOADED",
          tab_slug,
          new Date().getTime()
        );
      hasCalledLoadComplete.current = true;
    }
  }, [printMode, isFetchingCrashTrackAssetInfo, onLoadComplete]);

  const tableData = useMemo(() => {
    if (isFetchingCrashTrackAssetInfo || isErrorCrashTrackAssetInfo) return [];
    return [
      {
        label: t("tableFields.account"),
        value: thisUserOrganizationId || "-",
      },
      {
        label: t("tableFields.asset"),
        value: dataCrashTrackAssetInfo?.deviceid || "-",
      },
      {
        label: t("tableFields.assetDescription"),
        value: dataCrashTrackAssetInfo?.description || "-",
      },
      {
        label: t("tableFields.displayname"),
        value: dataCrashTrackAssetInfo?.displayname || "-",
      },
      ...(showDriver
        ? [
            {
              label: t("tableFields.drivername"),
              value: dataCrashTrackAssetInfo?.drivername || "-",
            },
          ]
        : []),
      {
        label: t("tableFields.make"),
        value: dataCrashTrackAssetInfo?.vehiclemake || "-",
      },
      {
        label: t("tableFields.model"),
        value: dataCrashTrackAssetInfo?.vehiclemodel || "-",
      },
      {
        label: t("tableFields.vin"),
        value: dataCrashTrackAssetInfo?.vehicleid || "-",
      },
      {
        label: t("tableFields.imei"),
        value: dataCrashTrackAssetInfo?.imei || "-",
      },
      // {
      //   label: t("tableFields.driverDescription"),
      //   value: dataCrashTrackAssetInfo?.driver_description || "-",
      // },
      {
        label: t("tableFields.groups"),
        value: dataCrashTrackAssetInfo?.groups || "-",
      },
    ];
  }, [
    thisUserOrganizationId,
    dataCrashTrackAssetInfo,
    isFetchingCrashTrackAssetInfo,
    isErrorCrashTrackAssetInfo,
  ]);

  if (isFetchingCrashTrackAssetInfo)
    return (
      <div className="flex-grow flex items-center justify-center min-h-64">
        <img src={svgIcons.LoadingAnimation} alt="" className="size-12" />
      </div>
    );

  if (isErrorCrashTrackAssetInfo) {
    return (
      <div className="flex flex-col items-center justify-center min-h-64 text-center">
        <p className="font-bold text-xl text-gray-400 px-8">
          {tCrashTrackDetails("tab_data_fetch_error")}
        </p>
      </div>
    );
  }

  return (
    <div className="printable-landscape-page flex flex-col gap-4 p-4">
      <h2 className="font-semibold text-lg">{t("heading")}</h2>
      <div className="flex flex-col bg-white rounded-lg">
        {tableData.map((tableDatum, i) => (
          <div
            className={`flex px-7 py-4${i > 0 ? " border-t border-t-border-gray" : ""}`}
            key={i}
          >
            <div className="min-w-64 max-w-64 font-semibold">
              {tableDatum.label}
            </div>
            <div className="flex-grow">{tableDatum.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
export default ScreenCrashTrackTabAssetInfo;
